import { useEffect } from "react";

import type { Doc } from "@convex/_generated/dataModel";
import { AttachmentPreview } from "./AttachmentPreview";

export interface EmailDetailProps {
  email: Doc<"emails">;
  onMarkAsRead: () => void;
  onBack?: () => void;
}

export function EmailDetail({ email, onMarkAsRead, onBack }: EmailDetailProps) {
  useEffect(() => {
    if (!email.isRead) {
      onMarkAsRead();
    }
  }, [email._id, email.isRead, onMarkAsRead]);

  const date = new Date(email.receivedAt);
  const formattedDate = date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });

  return (
    <div className="flex-1 overflow-y-auto p-4 md:p-6">
      {onBack && (
        <button
          onClick={onBack}
          className="mb-3 flex items-center gap-1 text-sm text-black/60 dark:text-white/60 md:hidden"
        >
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
          </svg>
          Back
        </button>
      )}
      <div className="mb-6 border-b border-black/10 pb-4 dark:border-white/10">
        <h2 className="text-xl font-bold text-black dark:text-white">{email.subject}</h2>
        <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-black/60 dark:text-white/60">
          <span>From: {email.from}</span>
          {email.cc && <span>Cc: {email.cc}</span>}
          <span>{formattedDate}</span>
        </div>
        {email.attachments.length > 0 && (
          <div className="mt-3 space-y-2">
            {email.attachments.map((att, i) => (
              <AttachmentPreview
                key={i}
                emailId={email._id}
                storageId={att.storageId}
                filename={att.filename}
                contentType={att.contentType}
                size={att.size}
              />
            ))}
          </div>
        )}
      </div>
      <div className="whitespace-pre-wrap text-sm leading-relaxed text-black/80 dark:text-white/80">
        {email.textBody ?? email.htmlBody ?? "(no content)"}
      </div>
    </div>
  );
}
